import React from 'react'
import {Box, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Typography} from '@mui/material'
import {css, useTheme} from '@emotion/react'
import {useTranslations} from 'next-intl'

import {KanaList} from './kanaData'
import Button from '../../../../components/elements/Button'
import {GameMode, KanaCategory} from './constants'
import BasicDialog from '../../../../components/elements/BasicDialog'


export default function SettingsDialog({isOpen, setting, onClose, onApply}) {
  const theme = useTheme()
  const styles = {
    content: css`
      display: flex;
      flex-direction: column;
      gap: 16px;
    `,
    section: css`
      margin-bottom: 16px;
    `,
    toggleGroup: css`
      display: flex;
      width: 100%;
      margin: 12px 0;
    `,
    categorySelection: css`
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    `,
    kanaGrid: css`
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 8px;
      margin-top: 12px;
    `,
    kanaItem: css`
      .MuiFormControlLabel-label {
        font-size: 0.9rem;
      }
    `
  }
  const t = useTranslations()

  const [mode, setMode] = React.useState(GameMode.Hiragana)
  const [selectedKanaSet, setSelectedKanaSet] = React.useState(new Set())

  React.useEffect(() => {
    setMode(setting.mode)
    setSelectedKanaSet(new Set(setting.selectedKanaIDs))
  }, [setting])

  const toggleKana = (id) => {
    setSelectedKanaSet((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const toggleCategory = (category) => {
    setSelectedKanaSet((prev) => {
      const newSet = new Set(prev)
      const categoryKana = KanaList.filter((kana) => kana.category === category)
      const allSelected = categoryKana.every((kana) => newSet.has(kana.id))
      if (allSelected) {
        // 全選中 => 全取消
        categoryKana.forEach((kana) => {
          newSet.delete(kana.id)
        })
      } else {
        // 部分或全未選中 => 全選
        categoryKana.forEach((kana) => {
          newSet.add(kana.id)
        })
      }
      return newSet
    })
  }

  const handleApply = () => {
    const selectedKanaIDs = Array.from(selectedKanaSet)
    onApply({mode, selectedKanaIDs})
  }

  const handleMode = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode)
    }
  }

  const kanaCategorySelections = [
    {value: KanaCategory.Seion, label: t('kana-connect.settings.category.seion')},
    {value: KanaCategory.Dakuon, label: t('kana-connect.settings.category.dakuon')},
    {value: KanaCategory.Handakuon, label: t('kana-connect.settings.category.handakuon')},
    {value: KanaCategory.YoOn, label: t('kana-connect.settings.category.yo-on')}
  ]

  const showKana = React.useCallback((kana) => {
    switch (mode) {
      case GameMode.Hiragana:
        return kana.hiragana
      case GameMode.Katakana:
        return kana.katakana
      case GameMode.Mixed:
        return `${kana.hiragana} / ${kana.katakana}`
    }
  }, [mode])

  const dialogActions = (
    <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
      <Button onClick={onClose}>{t('kana-connect.settings.actions.cancel')}</Button>
      <Button
        disabled={selectedKanaSet.size === 0}
        onClick={handleApply}
        color="primary"
      >
        {t('kana-connect.settings.actions.confirm')}
      </Button>
    </Box>
  )

  return (
    <BasicDialog
      title={t('kana-connect.settings.title')}
      open={isOpen}
      onClose={onClose}
      autoFullScreen
      actions={dialogActions}
    >
      <div css={styles.content}>
        <div css={styles.section}>
          <Typography variant="h6">{t('kana-connect.settings.mode')}</Typography>
          <RadioGroup
            css={styles.categorySelection}
            defaultValue={mode}
            value={mode}
            onChange={handleMode}
            row={true}
          >
            <FormControlLabel
              value={GameMode.Hiragana}
              control={<Radio/>}
              label={t('kana-connect.settings.mode-options.hiragana')}
            />
            <FormControlLabel
              value={GameMode.Katakana}
              control={<Radio/>}
              label={t('kana-connect.settings.mode-options.katakana')}
            />
            <FormControlLabel
              value={GameMode.Mixed}
              control={<Radio/>}
              label={t('kana-connect.settings.mode-options.mixed')}
            />
          </RadioGroup>
        </div>

        <div css={styles.section}>
          <Typography variant="h6">{t('kana-connect.settings.quick-select')}</Typography>
          <FormGroup css={styles.categorySelection} row={true}>
            {kanaCategorySelections.map(category => (
              <FormControlLabel
                key={category.value}
                control={
                  <Checkbox
                    checked={KanaList
                      .filter(kana => kana.category === category.value)
                      .every(kana => selectedKanaSet.has(kana.id))
                    }
                    onChange={() => toggleCategory(category.value)}
                  />
                }
                label={category.label}
              />
            ))}
          </FormGroup>
        </div>

        <div css={styles.section}>
          <Typography variant="h6">{t('kana-connect.settings.select')}</Typography>
          <FormGroup css={styles.kanaGrid}>
            {KanaList.map((kana) => (
              <FormControlLabel
                key={kana.id}
                css={styles.kanaItem}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedKanaSet.has(kana.id)}
                    onChange={() => toggleKana(kana.id)}
                  />
                }
                label={showKana(kana)}
              />
            ))}
          </FormGroup>
        </div>
      </div>
    </BasicDialog>
  )
}
